import React, { ReactNode } from 'react';
import classNames from 'classnames';
import type { SortOrder } from 'antd/es/table/interface';
import { Icon, IconType } from '@components/Icon';
import { SortingIcon } from '../Sorting';
import type { MadeColumnType } from '../hooks/useMakeColumns';
import { CHECKBOX_COLUMN_WIDTH, DEFAULT_COLUMN_WIDTH, EXPAND_COLUMN_WIDTH, SIZELESS_FLEX_GROW } from './constants';

interface HeaderCellProps {
    col: MadeColumnType;
    colIndex: number;
    checkboxColumnTitle?: ReactNode;
    filterDropdownOpenKey: string | null;
    filterTriggerRef: React.MutableRefObject<HTMLDivElement | null>;
    setFilterDropdownOpenKey: React.Dispatch<React.SetStateAction<string | null>>;
    getSortOrderForColumn: (col: MadeColumnType) => SortOrder | undefined;
    handleSortClick: (columnKey: string, order: SortOrder) => void;
}

export function HeaderCell({
    col,
    colIndex,
    checkboxColumnTitle,
    filterDropdownOpenKey,
    filterTriggerRef,
    setFilterDropdownOpenKey,
    getSortOrderForColumn,
    handleSortClick,
}: HeaderCellProps) {
    const isExpandColumn = col.key === 'virtual-table-expand';
    const isCheckbox = col.key === 'virtual-table-checkbox';
    const hasAction = isCheckbox || !!col.sorter || !!col.filterDropdown;
    const filteredVal = col.filteredValue;
    const hasFilterActive =
        !!col.filterDropdown && (Array.isArray(filteredVal) ? filteredVal.length > 0 : !!filteredVal);
    const cellWidth = isExpandColumn
        ? (col.width ?? EXPAND_COLUMN_WIDTH)
        : isCheckbox
          ? CHECKBOX_COLUMN_WIDTH
          : (col.width ?? DEFAULT_COLUMN_WIDTH);
    const flexGrow = isExpandColumn
        ? (col.width ?? EXPAND_COLUMN_WIDTH)
        : isCheckbox
          ? CHECKBOX_COLUMN_WIDTH
          : (col.width ?? SIZELESS_FLEX_GROW);

    return (
        <div
            key={`${col.key}${col.title}${colIndex}`}
            className={classNames(
                'virtual_table_header_cell',
                hasAction && 'virtual_table_header_cell_has_action',
                hasFilterActive && 'virtual_table_header_cell_filter_active',
            )}
            style={{
                width: cellWidth,
                minWidth: cellWidth,
                flexGrow: flexGrow,
            }}
        >
            {isExpandColumn ? null : isCheckbox ? (
                checkboxColumnTitle
            ) : (
                <>
                    <span className="virtual_table_header_title">
                        {typeof col.title === 'function' ? col.title({}) : col.title}
                    </span>
                    {col.sorter && (
                        <span
                            className="virtual_table_sort_trigger"
                            onClick={() => {
                                const current = getSortOrderForColumn(col);
                                const next = current === 'ascend' ? 'descend' : 'ascend';
                                handleSortClick(String(col.key), next);
                            }}
                        >
                            <SortingIcon sortOrder={getSortOrderForColumn(col) ?? 'ascend'} />
                        </span>
                    )}
                    {col.filterDropdown && (
                        <div
                            className="virtual_table_filter_wrapper"
                            ref={filterDropdownOpenKey === String(col.key) ? filterTriggerRef : undefined}
                        >
                            <span
                                className="virtual_table_filter_trigger"
                                onClick={() =>
                                    setFilterDropdownOpenKey(prev =>
                                        prev === String(col.key) ? null : String(col.key),
                                    )
                                }
                            >
                                {col.filterIcon?.(!!col.filteredValue) ?? <Icon type={IconType.FILTER_REDESIGN} />}
                            </span>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
